import ActionButton from 'components/ActionButton';
import AddUserForEntityModal from 'components/AddUserForEntityModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { getPageLimit, showError, showSuccess, updatePageLimit } from 'utils';
import styles from './index.module.scss';

const filters: { [p: string]: any; page: number } = {
  page: 0,
};

const UsersTable = props => {
  const {
    entityId,
    entityKey,
    entityName,
    addUserAPI,
    deleteUserAPI,
    fetchUsersAPI,
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Record<string, any>>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const fetchUsersFn = async () => {
    setLoading(true);
    try {
      const response = await fetchUsersAPI({
        ...filters,
        [entityKey]: entityId,
        limit: getPageLimit(),
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersFn();
  }, []);

  const deleteUserFn = async (userId: string) => {
    try {
      await deleteUserAPI(userId, entityId);
      await fetchUsersFn();
      showSuccess('User removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'UserID',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/users/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'full_name',
      sortable: true,
    },
    {
      name: 'Email',
      selector: 'email',
      sortable: true,
    },
    {
      name: 'Phone',
      selector: 'phone',
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => deleteUserFn(row.objectId)}
          />
        </div>
      ),
    },
  ], []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchUsersFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  if (loading) return <Loading type={'mini'} />;

  return (
    <div className={styles['users-container']}>
      {modalOpen && (
        <AddUserForEntityModal
          entityId={entityId}
          addUserAPI={addUserAPI}
          onSuccess={fetchUsersFn}
          toggleModal={() => setModalOpen(!modalOpen)}
        />
      )}
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>{`${entityName} Users`}</div>
        <ActionButton
          active={true}
          isLoading={false}
          onClick={() => setModalOpen(true)}
          text={<>User <span className={'ml-3'}>+</span></>}
        />
      </div>
      <DataTable
        columns={columns}
        noHeader={true}
        data={data.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField="objectId"
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.total}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  );
};

export default UsersTable;